// Lib
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { shouldShowFeatureSuggestion } from '../feature/elementFeatureUtils';
import { useTranslator } from '../../components/i18n/useTranslator';

// Selectors
import { getDuplicateOriginalIdSelector } from '../duplicate/elementDuplicateSelector';

// Actions
import { convertDuplicateToClone } from '../actions/elementShortcutActions';
import { elementSuggestFeature, elementUnsuggestFeature } from '../suggestion/suggestionActions';
import { convertElementsToDocument } from '../document/documentActions';

// Hooks
import useUpdateEffect from '../../utils/react/useUpdateEffect';

// Components
import FeatureSuggestions from '../suggestion/FeatureSuggestions';
import ElementFeatureSuggestion from '../suggestion/ElementFeatureSuggestion';

// Constants
import { ELEMENT_FEATURES } from '../feature/elementFeatureConstants';
import { getHasClones } from '../../../common/elements/utils/elementPropertyUtils';
import ConvertToDocumentFeatureSuggestion from '../suggestion/ConvertToDocumentFeatureSuggestion';

const mapStateToProps = createStructuredSelector({
    copiedElementId: getDuplicateOriginalIdSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSuggestClone: (id) => dispatch(elementSuggestFeature({ id, feature: ELEMENT_FEATURES.CLONE })),
    dispatchUnsuggestClone: (id) => dispatch(elementUnsuggestFeature({ id, feature: ELEMENT_FEATURES.CLONE })),
    dispatchConvertDuplicateToClone: (element, transactionId) =>
        dispatch(convertDuplicateToClone(element, transactionId)),
    dispatchConvertElementsToDocument: (args) => dispatch(convertElementsToDocument(args)),
});

const shouldShowCloneSuggestion = (element, isClone) =>
    shouldShowFeatureSuggestion(ELEMENT_FEATURES.CLONE)(element) && !getHasClones(element) && !isClone;

const CardFeatureSuggestions = (props) => {
    const { elementId, copiedElementId, dispatchSuggestClone, dispatchUnsuggestClone, dispatchConvertDuplicateToClone } = props;

    // Get translator for card feature suggestions
    const translate = useTranslator('elements', 'elementTypes.card.featureSuggestions');

    const convertToDocumentQuestion = translate('document.question', 'Convert to document');
    const keepCloneQuestion = translate('clone.question', 'Keep this copy in sync?');

    const suggestions = useMemo(
        () => [
            {
                feature: ELEMENT_FEATURES.DOCUMENT,
                component: ConvertToDocumentFeatureSuggestion,
                shouldShow: shouldShowFeatureSuggestion(ELEMENT_FEATURES.DOCUMENT),
                question: convertToDocumentQuestion,
            },
            {
                feature: ELEMENT_FEATURES.CLONE,
                component: ElementFeatureSuggestion,
                shouldShow: shouldShowCloneSuggestion,
                question: keepCloneQuestion,
                onConfirm: ({ element }, transactionId) =>
                    dispatchConvertDuplicateToClone(element, transactionId),
            },
        ],
        [convertToDocumentQuestion, keepCloneQuestion, dispatchConvertDuplicateToClone]
    );

    React.useEffect(() => {
        if (copiedElementId) dispatchSuggestClone(elementId);
    }, [copiedElementId]);

    useUpdateEffect(() => {
        if (!copiedElementId) dispatchUnsuggestClone(elementId);
    }, [copiedElementId]);

    return <FeatureSuggestions {...props} possibleSuggestions={suggestions} />;
};

CardFeatureSuggestions.propTypes = {
    elementId: PropTypes.string,
    copiedElementId: PropTypes.string,
    dispatchSuggestClone: PropTypes.func,
    dispatchUnsuggestClone: PropTypes.func,
    dispatchConvertDuplicateToClone: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardFeatureSuggestions);
